/**
 * WHO MAY ADMINISTER CHAMBERS GOVERNANCE.
 *
 * The constitution, committees and policy-management panels are gated on the API by
 * `requireChambersAdmin`, which demands `memberType === 'admin'` EXACTLY — and it does so on
 * `router.use(...)`, so it covers GET as well as writes (constitution.routes.ts:9,
 * committees.routes.ts:9, policies.routes.ts:8).
 *
 * Staff inherit the admin sidebar (Sidebar.tsx shares one branch for admin and staff) and the
 * `/admin` route group admitted `['admin', 'staff']`, so a staff member was shown three menu
 * items whose pages could only ever answer 403 on LOAD. Worse than a dead end: every refusal
 * writes a `permission_denied` audit row (rbac.middleware.ts:94-102), so staff opening a menu
 * item in good faith filled the chambers' compliance record with denials.
 *
 * This hides what the server refuses. It does NOT widen anything — whether staff SHOULD be able
 * to read governance is an open product question and belongs to the API, not to the menu.
 * Staff keep their own policy obligations: acknowledgement runs through `/me/policies`
 * (routes.ts:162), which is member-facing and untouched by this rule.
 *
 * The server stays authoritative. This decides only whether a control is DRAWN, so that nobody
 * is walked into a page whose load can never succeed (Rule #8). Mirrored by
 * governance-admin-only.guard.test.ts, which derives the API side rather than trusting this list.
 */

/** Member types `requireChambersAdmin` accepts. It is an exact equality check on the API. */
const GOVERNANCE_ADMIN_ROLES = ['admin'] as const;

export function canAdministerGovernance(memberType: string | null | undefined): boolean {
  return (GOVERNANCE_ADMIN_ROLES as readonly string[]).includes(memberType ?? '');
}

/**
 * Admin pages whose whole API router is behind `requireChambersAdmin`, keyed by the API mount
 * the page reads. The guard test derives this same set from the API source and fails if a new
 * admin-only router appears without a decision about its menu item.
 */
export const GOVERNANCE_ADMIN_PAGES: Readonly<Record<string, string>> = {
  '/constitution': '/admin/constitution',
  '/committees': '/admin/committees',
  '/policies': '/admin/policies',
};
